import { useEffect, useState, type ReactNode } from 'react';

/**
 * „Was ist neu?" — die Zusammenfassung der letzten Fassungen.
 *
 * Laedt `public/changelog-summary.md` und zeigt sie ab der ersten
 * `## v`-Ueberschrift. **Bewusst ohne Markdown-Bibliothek:** unterstuetzt wird
 * genau die Struktur, die diese eine Datei hat — Ueberschriften, Aufzaehlung,
 * `**fett**`. Eine Abhaengigkeit fuer drei Formatierungen waere der falsche
 * Tausch, und das Buendel wird beim Bauen ohnehin schon als zu gross gemeldet.
 *
 * Uebernommen aus ojfm (`frontend/src/components/changelogSummary.jsx`) und
 * absichtlich nah am Original gehalten: dieselbe Datei, dieselbe Struktur,
 * dieselbe Darstellung. Wer beide Anwendungen kennt, soll nicht umdenken.
 */

/** Fuehrende `---`-Kopfzeilen entfernen. */
function ohneVorspann(md: string): string {
  if (!md.startsWith('---')) return md;
  const ende = md.indexOf('\n---', 3);
  if (ende === -1) return md;
  const zeilenumbruch = md.indexOf('\n', ende + 1);
  return zeilenumbruch === -1 ? '' : md.slice(zeilenumbruch + 1);
}

/** `**fett**` wird fett, der Rest bleibt Text. */
function mitFett(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((teil, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-slate-800">
        {teil}
      </strong>
    ) : (
      <span key={i}>{teil}</span>
    ),
  );
}

/** `v1.0.137 (2026-09-18)` — die Nummer gross, das Datum klein daneben. */
function Ueberschrift({ text }: { text: string }) {
  const treffer = /^(.*?)\s*\(([^)]+)\)\s*$/.exec(text);
  return (
    <div className="mt-5 flex items-baseline gap-2 first:mt-0">
      <span className="text-sm font-semibold text-slate-800">
        {treffer ? treffer[1] : text}
      </span>
      {treffer && <span className="text-xs text-slate-400">{treffer[2]}</span>}
    </div>
  );
}

export function useChangelog(): { text: string | null; laedt: boolean } {
  const [text, setText] = useState<string | null>(null);
  const [laedt, setLaedt] = useState(true);

  useEffect(() => {
    let abgebrochen = false;
    fetch('/changelog-summary.md')
      .then((a) => (a.ok ? a.text() : null))
      .then((roh) => {
        if (abgebrochen) return;
        setText(roh ? ohneVorspann(roh) : null);
        setLaedt(false);
      })
      .catch(() => {
        if (!abgebrochen) setLaedt(false);
      });
    return () => {
      abgebrochen = true;
    };
  }, []);

  return { text, laedt };
}

/** Der Text als Absaetze und Aufzaehlungen. */
export function ChangelogInhalt({ text }: { text: string }) {
  const bloecke: ReactNode[] = [];
  let aufzaehlung: string[] = [];

  const aufzaehlungSchliessen = () => {
    if (aufzaehlung.length === 0) return;
    bloecke.push(
      <ul key={`l-${bloecke.length}`} className="mt-1.5 space-y-1.5">
        {aufzaehlung.map((zeile, i) => (
          <li key={i} className="flex gap-2 text-sm leading-relaxed text-slate-600">
            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-300" />
            <span>{mitFett(zeile)}</span>
          </li>
        ))}
      </ul>,
    );
    aufzaehlung = [];
  };

  for (const zeile of text.split('\n')) {
    const roh = zeile.trim();
    if (roh.startsWith('## ')) {
      aufzaehlungSchliessen();
      bloecke.push(<Ueberschrift key={`h-${bloecke.length}`} text={roh.slice(3)} />);
    } else if (roh.startsWith('- ')) {
      aufzaehlung.push(roh.slice(2));
    } else if (roh.length > 0) {
      aufzaehlungSchliessen();
      bloecke.push(
        <p key={`p-${bloecke.length}`} className="mt-2 text-sm leading-relaxed text-slate-600">
          {mitFett(roh)}
        </p>,
      );
    }
  }
  aufzaehlungSchliessen();

  return <>{bloecke}</>;
}
